import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    primary: {
      main: '#2D3648', // Deep blue-grey
      light: '#4A5568', // Medium blue-grey
      dark: '#1A202C', // Darker shade for hover states
    },
    secondary: {
      main: '#718096', // Light blue-grey
      light: '#A0AEC0', // Lighter shade
      dark: '#4A5568', // Darker shade
    },
    background: {
      default: '#F7FAFC', // Very light blue-grey
      paper: '#FFFFFF', // White
      surface: '#EDF2F7', // Light grey
    },
    text: {
      primary: '#2D3748', // Dark blue-grey
      secondary: '#718096', // Medium grey
    },
    error: {
      main: '#E53E3E', // Red
    },
    success: {
      main: '#48BB78', // Green
    },
    warning: {
      main: '#ED8936', // Orange
    },
    grey: {
      50: '#F7FAFC',
      100: '#EDF2F7',
      200: '#E2E8F0',
      300: '#CBD5E0',
      400: '#A0AEC0',
      500: '#718096',
      600: '#4A5568',
      700: '#2D3748',
      800: '#1A202C',
      900: '#171923',
    },
  },
  components: {
    MuiDataGrid: {
      styleOverrides: {
        columnHeaderTitle: {
          fontWeight: 700,
        },
        columnHeader: {
          '&:focus, &:focus-within': {
            outline: 'none',
          },
        },
        columnSeparator: {
          cursor: 'col-resize !important',
          touchAction: 'none',
          '&.MuiDataGrid-columnSeparator--resizable': {
            cursor: 'col-resize !important',
            '&:hover': {
              color: '#2D3648',
              opacity: 1,
            },
          },
          '&.MuiDataGrid-columnSeparator--resizing': {
            color: '#2D3648 !important',
            opacity: 1,
          },
        },
        iconSeparator: {
          display: 'block',
        },
        footerContainer: {
          position: 'sticky',
          bottom: 0,
          zIndex: 2,
          backgroundColor: '#FFFFFF',
          borderTop: '1px solid #E2E8F0',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          padding: '8px 16px',
          transition: 'background-color 0.15s ease-in-out, color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
          '&:hover': {
            backgroundColor: '#1A202C',
            color: '#fff',
          },
        },
        contained: {
          backgroundColor: '#2D3648',
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: '#1A202C',
            color: '#fff',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '& fieldset': {
              borderColor: '#E2E8F0',
            },
            '&:hover fieldset': {
              borderColor: '#2D3648',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#2D3648',
            },
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #E2E8F0',
          padding: '4px 8px', // Minimal padding for table cells
        },
        head: {
          backgroundColor: '#F7FAFC',
          color: '#2D3748',
          fontWeight: 700,
          padding: '4px 8px', // Minimal padding for header cells
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: '#F7FAFC',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.15s ease-in-out, color 0.15s ease-in-out',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.15s ease-in-out, color 0.15s ease-in-out',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 40,
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          margin: 0,
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          animation: 'circular-rotate 1.4s linear infinite',
          '& svg': {
            display: 'block',
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        // Global table/grid action buttons: text-like by default, underline on hover.
        '.MuiDataGrid-cell .MuiButton-root, .MuiTableCell-root .MuiButton-root': {
          background: 'transparent !important',
          border: 'none !important',
          boxShadow: 'none !important',
          minWidth: '0 !important',
          padding: '0 !important',
          borderRadius: '0 !important',
          textTransform: 'none !important',
          fontWeight: '700 !important',
          color: '#2D3648 !important',
          textDecoration: 'none !important',
        },
        '.MuiDataGrid-cell .MuiButton-root:hover, .MuiTableCell-root .MuiButton-root:hover': {
          background: 'transparent !important',
          color: '#1A202C !important',
          textDecoration: 'underline !important',
          textUnderlineOffset: '2px',
        },
        '@keyframes circular-rotate': {
          '0%': {
            transform: 'rotate(0deg)',
          },
          '100%': {
            transform: 'rotate(360deg)',
          },
        },
      },
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      color: '#2D3748',
    },
    h2: {
      fontWeight: 700,
      color: '#2D3748',
    },
    h3: {
      fontWeight: 600,
      color: '#2D3748',
    },
    h4: {
      fontWeight: 600,
      color: '#2D3748',
    },
    h5: {
      fontWeight: 600,
      color: '#2D3748',
    },
    h6: {
      fontWeight: 600,
      color: '#2D3748',
    },
    body1: {
      color: '#4A5568',
    },
    body2: {
      color: '#718096',
    },
  },
  shape: {
    borderRadius: 8,
  },
});

export default theme; 